import React, { useEffect, useState } from "react";

import Template from "@layout/Template";
import { LayoutProps } from "@layout/types";
import { useLocation } from "@reach/router";

import { COOKIE_CONSENT_CHANGED_EVENT, getCookieAccepted } from "@/account/user";
import { WebsiteSectionUrl } from "@/components/constants";
import AuthorizationFormContextProvider from "@/contexts/AuthorizationForm";
import GlobalContextProvider from "@/contexts/Global";
import { ImageSizesContext } from "@/contexts/ImageSizes";
import { PageCanonicalContext } from "@/contexts/PageCanonical";
import { SmartCaptchaProvider } from "@/contexts/SmartCaptcha";
import UserContextProvider from "@/contexts/User";
import { disableYandexMetrikaAndClearData } from "@/marketing/yandexMetrika";
import { isBrowser } from "@/utils/env";
import { buildCanonicalUrl } from "@/utils/seo/canonical";

import FontsHelmet from "./helmets/fontsHelmet";
import MetaHelmet from "./helmets/metaHelmet";
import MindboxHelmet from "./helmets/mindboxHelmet";
import OstrioAnalyticsHelmet from "./helmets/ostrioAnalyticsHelmet";
import OtherScriptsHelmet from "./helmets/otherScriptsHelmet";
import WebViewStyleHelmet from "./helmets/webViewStyleHelmet";
import YandexMetrikaHelmet from "./helmets/yandexMetrikaHelmet";
import useScrollManagement from "./hooks/useScrollManagement";
import SubdomainPage from "./subdomainPage";

import "./styles.scss";

// Когда true, Метрика всегда включена и не зависит от выбора в cookie banner.
// При этом сам выбор пользователя ("true"/"false") по-прежнему честно сохраняется в логах.
const FORCE_ENABLE_YANDEX_METRIKA = true;

const getInitialYandexMetrikaConsent = (): boolean => {
  if (!isBrowser()) {
    return true;
  }

  if (FORCE_ENABLE_YANDEX_METRIKA) {
    // Принудительно разрешаем Метрику независимо от cookie_accepted.
    return true;
  }

  return getCookieAccepted() !== "false";
};

const Layout = ({ children, pageContext }: LayoutProps) => {
  const [isYandexMetrikaAllowed, setIsYandexMetrikaAllowed] = useState<boolean>(
    getInitialYandexMetrikaConsent,
  );

  useEffect(() => {
    if (!isBrowser()) {
      return undefined;
    }

    const updateMetrikaConsent = () => {
      if (FORCE_ENABLE_YANDEX_METRIKA) {
        // Держим Метрику включенной даже если пользователь нажал "Отказаться".
        setIsYandexMetrikaAllowed(true);
        return;
      }

      const cookieAccepted = getCookieAccepted();
      setIsYandexMetrikaAllowed(cookieAccepted !== "false");
    };

    updateMetrikaConsent();
    window.addEventListener("storage", updateMetrikaConsent);
    document.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, updateMetrikaConsent);

    return () => {
      window.removeEventListener("storage", updateMetrikaConsent);
      document.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, updateMetrikaConsent);
    };
  }, []);

  useEffect(() => {
    if (
      !isBrowser()
      || isYandexMetrikaAllowed
      || FORCE_ENABLE_YANDEX_METRIKA
    ) {
      // В форс-режиме не отключаем и не чистим данные Метрики.
      return;
    }

    disableYandexMetrikaAndClearData();
  }, [isYandexMetrikaAllowed]);

  useScrollManagement({
    pathsToPreservePosition: [
      WebsiteSectionUrl.Catalog,
      WebsiteSectionUrl.Article,
      WebsiteSectionUrl.Category,
      WebsiteSectionUrl.Goroskop,
      WebsiteSectionUrl.Gid,
    ],
    renderedContentPath: children.props.path,
  });

  // Путь страницы берём из роутера, а не из пропа `path`: в сборке Gatsby монтирует
  // страницу как `<RouteHandler path="/*">` (cache-dir/static-entry.js), поэтому в проп
  // приходит literal `/*`, и адреса узлов Schema.org в статическом HTML собирались с ним.
  // `useLocation` в SSR приходит из `<ServerLocation url={pagePath}>` того же файла и верен.
  // Раздаём канонический адрес вниз, разметке Schema.org (задача 4555).
  const { pathname } = useLocation();
  const pageCanonical = pathname ? buildCanonicalUrl(pathname) : null;

  return (
    <PageCanonicalContext.Provider value={pageCanonical}>
      {/* размеры картинок Strapi для `<img>` из текста редактора */}
      <ImageSizesContext.Provider value={pageContext?.imageSizes ?? null}>
        {isBrowser() && (
          <>
            <FontsHelmet />
            <MetaHelmet />
            {isYandexMetrikaAllowed && <YandexMetrikaHelmet />}
            {isYandexMetrikaAllowed && <OstrioAnalyticsHelmet />}
            <MindboxHelmet />
            <OtherScriptsHelmet />
            <WebViewStyleHelmet />
          </>
        )}
        {process.env.GATSBY_SUBDOMAIN ? (
          <UserContextProvider>
            <SmartCaptchaProvider>
              <SubdomainPage>{children}</SubdomainPage>
            </SmartCaptchaProvider>
          </UserContextProvider>
        ) : (
          <UserContextProvider>
            <GlobalContextProvider>
              <SmartCaptchaProvider>
                <AuthorizationFormContextProvider>
                  <Template>{children}</Template>
                </AuthorizationFormContextProvider>
              </SmartCaptchaProvider>
            </GlobalContextProvider>
          </UserContextProvider>
        )}
      </ImageSizesContext.Provider>
    </PageCanonicalContext.Provider>
  );
};

export default Layout;
